/**
 * AuthContext
 *
 * Listens for a JWT from the parent app via postMessage (iframe integration).
 * When VITE_ENABLE_AUTH=false (local dev / staging pre-integration), auth is
 * bypassed entirely — the app works without a token.
 *
 * Parent app usage:
 * iframeEl.contentWindow.postMessage({ type: 'NEMO_AUTH_EVENT', token: 'eyJ...' }, '*')
 *
 * To restrict which origins can send tokens, set VITE_PARENT_APP_ORIGIN in .env.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { jwtDecode } from 'jwt-decode'

import { setToken } from '@/api/tokenStore'
import i18n, { LANGUAGE_KEY } from '@/i18n'

/** JWT claims we read from the parent app token. All optional — fall back gracefully. */
interface NemoClaims {
  legal_ref?: string        // e.g. "CA-SC1999,c33" — pre-populate document filter
  interface_language?: string  // e.g. "en", "fr", "zh" — set UI language
  region?: string           // e.g. "US", "CN", "AU", "EU" — regional home URL
}

/** Read legal_ref and language from iframe URL query params (set by the parent app). */
function readUrlParams(): { legalRef: string | null; language: string | null } {
  const params = new URLSearchParams(window.location.search)
  return {
    legalRef: params.get('leg_ref')?.trim() || null,
    language: params.get('language')?.trim()?.toLowerCase() || null,
  }
}

function applyLanguage(lang: string | null | undefined): void {
  if (lang && ['en', 'fr', 'zh'].includes(lang) && lang !== i18n.language) {
    i18n.changeLanguage(lang)
    localStorage.setItem(LANGUAGE_KEY, lang)
    document.documentElement.lang = lang
  }
}

function applyLanguageFromToken(token: string): void {
  try {
    const claims = jwtDecode<NemoClaims>(token)
    console.debug(`[AUTH] JWT interface_language: ${claims.interface_language ?? 'not set'}`)
    console.debug(`[AUTH] JWT region: ${claims.region ?? 'not set'}`)
    applyLanguage(claims.interface_language?.toLowerCase())
  } catch {
    // malformed token — leave language as-is
  }
}

function extractLegalRef(token: string): string | null {
  try {
    const claims = jwtDecode<NemoClaims>(token)
    return claims.legal_ref?.trim() || null
  } catch {
    return null
  }
}

function extractRegion(token: string): string | null {
  try {
    const claims = jwtDecode<NemoClaims>(token)
    return claims.region?.trim()?.toUpperCase() || null
  } catch {
    return null
  }
}

interface AuthContextType {
  /** True when auth is disabled OR a valid token has been received */
  isReady: boolean
  /** The raw JWT string, null if not yet received or auth is disabled */
  token: string | null
  /** legal_ref to pre-select on startup — from iframe URL params or JWT claims */
  preloadLegalRef: string | null
  /** 2-letter country code from JWT region claim (e.g. "US", "CN", "AU", "FR") */
  region: string | null
}

const AuthContext = createContext<AuthContextType>({
  isReady: true,
  token: null,
  preloadLegalRef: null,
  region: null,
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const authEnabled = import.meta.env.VITE_ENABLE_AUTH === 'true'
  const allowedOrigin = import.meta.env.VITE_PARENT_APP_ORIGIN ?? '*'

  const [token, setTokenState] = useState<string | null>(null)
  const [region, setRegion] = useState<string | null>(null)
  const [preloadLegalRef, setPreloadLegalRef] = useState<string | null>(() => {
    // Read legal_ref + language from iframe URL params on mount (set by parent in iframe src)
    const { legalRef, language } = readUrlParams()
    if (legalRef) console.debug(`[AUTH] URL param legal_ref="${legalRef}"`)
    if (language) {
      console.debug(`[AUTH] URL param language="${language}"`)
      applyLanguage(language)
    }
    return legalRef
  })

  useEffect(() => {
    if (!authEnabled) {
      console.debug('[AUTH] Auth disabled, skipping postMessage listener')
      return
    }

    console.debug('[AUTH] Auth enabled, listening for postMessage from origin:', allowedOrigin)

    function handleMessage(event: MessageEvent) {
      // Validate origin
      if (allowedOrigin !== '*' && event.origin !== allowedOrigin) {
        console.debug('[AUTH] Origin rejected:', event.origin, '!==', allowedOrigin)
        return
      }

      if (
        event.data &&
        typeof event.data === 'object' &&
        event.data.type === 'nemo:auth' &&
        typeof event.data.token === 'string'
      ) {
        console.debug('[AUTH] Token received, length:', event.data.token.length)
        applyLanguageFromToken(event.data.token)
        // Only use JWT legal_ref if URL params didn't provide one
        setPreloadLegalRef(prev => prev ?? extractLegalRef(event.data.token))
        setRegion(extractRegion(event.data.token))
        setTokenState(event.data.token)
      } else {
        console.debug('[AUTH] Message ignored — does not match expected format { type: "nemo:auth", token: "..." }')
      }
    }

    // 1. Start listening for the parent's reply
    window.addEventListener('message', handleMessage)

    // 2. Announce to the parent that we are ready to receive the token!
    // The code requires handshake with parent app (as per code from parent app)
    if (window.parent !== window) {
      console.debug('[AUTH] Sending nemo:ready to parent...')
      window.parent.postMessage({ type: 'nemo:ready' }, allowedOrigin)
    }

    return () => window.removeEventListener('message', handleMessage)
  }, [authEnabled, allowedOrigin])

  // Keep module store in sync when token state changes
  useEffect(() => {
    setToken(token)
  }, [token])

  const isReady = !authEnabled || token !== null

  return (
    <AuthContext.Provider value={{ isReady, token, preloadLegalRef, region }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  return useContext(AuthContext)
}